/* eslint-disable @typescript-eslint/no-explicit-any */
import { type GameState, RoundStage } from './definitions';

export interface CalcStat {
	count: number;
	sum: number;
	avg: number;
	min: number;
	max: number;
	firstValue: number;
	streak: number;
	streakMax: number;
	createDate: string;
	modifiedDate: string;
	lastValue: number;
}

export interface Stats {
	[key: string]: Stats | CalcStat | string | number | undefined;
}

type StatStore = Record<string, unknown>;

function getWinPlace(gameState: GameState, position: number): number {
	const scores = [...gameState.score];
	const playerScore = scores[position];
	scores.sort((a, b) => a - b);
	return scores.indexOf(playerScore) + 1;
}

export function getRoundStats(gameState: GameState, localPlayerPosition = 0, stats?: Stats): Stats {
	//// is not makeing a full copy of stats. might as well not copy at all and use stats = stats ?? {};
	// if (!stats) {
	// 	stats = {};
	// } else {
	// 	stats = {
	// 		...stats,
	// 	};
	// }
	const store = (stats ?? {}) as StatStore;

	const isRoundOver = gameState.roundStage === RoundStage.RoundEnd;

	if (!isRoundOver) {
		return store as Stats;
	}

	const level = gameState.level as string;

	//get current round
	const round = gameState.rounds[gameState.rounds.length - 1];

	for (const player of gameState.players) {
		const position = player.position;
		let playerName = player.name;
		const isLocalPlayer = position === localPlayerPosition;

		const categories: string[] = [];

		if (level) {
			categories.push(level); //push level as category
		}

		if (isLocalPlayer) {
			playerName = 'You'; // For local player, we can use a generic name
		}

		if (playerName === 'You' || playerName === 'Bob') {
			categories.push('All'); //also push to all category
		}

		if (!store[playerName]) {
			store[playerName] = {};
		}

		const pStats = store[playerName] as StatStore;

		insertStats(pStats, categories, 'avatar', player.avatar); //raw stat
		insertStats(pStats, categories, 'skill', player.skill); //raw stat

		insertCalcStats(pStats, categories, 'played_round', 1); //might not be needed, but nice for readability

		const points_round = round.score[position] ?? 0;
		insertCalcStats(pStats, categories, 'points_round', points_round);

		const zeroPointsRound = round.score[position] === 0 ? 1 : 0;
		insertCalcStats(pStats, categories, 'zero_points_round', zeroPointsRound);

		const shootTheMoon = round.moonShooter === position ? 1 : 0;
		insertCalcStats(pStats, categories, 'moon_shots', shootTheMoon);

		const heartPointsRound = round.heartPoints?.[position] ?? 0;
		insertCalcStats(pStats, categories, 'heart_points_round', heartPointsRound);

		const queenSpadesPointsRound = round.queenSpadesPoints?.[position] ?? 0;
		insertCalcStats(pStats, categories, 'queen_spades_points_round', queenSpadesPointsRound);

		const queenSpadesRound = queenSpadesPointsRound === 13 ? 1 : 0;
		insertCalcStats(pStats, categories, 'queen_spades_round', queenSpadesRound);

		const moonPointsRound = round.moonPoints?.[position] ?? 0;
		insertCalcStats(pStats, categories, 'moon_points_round', moonPointsRound);

	}

	return store as Stats;
}

export function getMatchStats(gameState: GameState, localPlayerPosition = 0, stats?: Stats): Stats {
	const store = (stats ?? {}) as StatStore;

	const gameIsOver = gameState.roundStage === RoundStage.GameOver;

	if (!gameIsOver) {
		return store as Stats;
	}

	const level = gameState.level as string;

	for (const player of gameState.players) {
		const position = player.position;
		let playerName = player.name;
		const isLocalPlayer = position === localPlayerPosition;

		const categories: string[] = [];

		if (level) {
			categories.push(level); //push level as category
		}

		if (isLocalPlayer) {
			playerName = 'You'; // For local player, we can use a generic name
		}

		if (playerName === 'You' || playerName === 'Bob') {
			categories.push('All'); //also push to all category
		}

		if (!store[playerName]) {
			store[playerName] = {};

		}

		const pStats = store[playerName] as StatStore;

		insertStats(pStats, categories, 'avatar', player.avatar); //raw stat
		insertStats(pStats, categories, 'skill', player.skill); //raw stat

		insertCalcStats(pStats, categories, 'played_match', 1); //might not be needed, but nice for readability

		const points_match = gameState.score[position] ?? 0;
		insertCalcStats(pStats, categories, 'points_match', points_match);

		const isZeroPointsMatch = points_match === 0 ? 1 : 0;
		insertCalcStats(pStats, categories, 'zero_points_match', isZeroPointsMatch);

		const points_match_under_5 = points_match < 5 ? 1 : 0;
		insertCalcStats(pStats, categories, 'points_match_under_5', points_match_under_5);

		const points_match_under_10 = points_match < 10 ? 1 : 0;
		insertCalcStats(pStats, categories, 'points_match_under_10', points_match_under_10);

		const points_match_under_20 = points_match < 20 ? 1 : 0;
		insertCalcStats(pStats, categories, 'points_match_under_20', points_match_under_20);

		//placement
		const placement = getWinPlace(gameState, position);

		//is win
		const isWin = placement === 1 ? 1 : 0; //first place = win
		insertCalcStats(pStats, categories, 'win', isWin);

		const isLoss = placement === 4 ? 1 : 0; //fourth place = loss
		insertCalcStats(pStats, categories, 'loss', isLoss);

		const isFirstPlace = placement === 1 ? 1 : 0; //first place
		insertCalcStats(pStats, categories, 'first_place', isFirstPlace);

		const isSecondPlace = placement === 2 ? 1 : 0; //second place
		insertCalcStats(pStats, categories, 'second_place', isSecondPlace);

		const isThirdPlace = placement === 3 ? 1 : 0; //third place
		insertCalcStats(pStats, categories, 'third_place', isThirdPlace);

		const isFourthPlace = placement === 4 ? 1 : 0; //fourth place
		insertCalcStats(pStats, categories, 'fourth_place', isFourthPlace);

	}

	return store as Stats;
}

function insertStats(stats: StatStore, categories: string[] | string, key: string, value: string | number | undefined) {

	//If no categories are provided, insert directly into stats
	if (!categories || categories.length === 0) {
		stats[key] = value;
		return;
	}

	//If categories is a string, convert to array
	if (typeof categories === 'string') {
		categories = [categories];
	}

	//Insert into each category
	for (const category of categories) {
		if (!stats[category]) {
			stats[category] = {

				level: category,
			};
		}

		(stats[category] as StatStore)[key] = value;
	}
}

function insertCalcStats(stats: StatStore, categories: string[] | string, key: string, value: number) {

	//If no categories are provided, insert directly into stats
	if (!categories || categories.length === 0) {
		insertCalcStat(stats, key, value);
		return;
	}

	//If categories is a string, convert to array
	if (typeof categories === 'string') {
		categories = [categories];
	}

	//Insert into each category
	for (const category of categories) {
		if (!stats[category]) {
			stats[category] = {

				level: category,
			};
		}

		insertCalcStat(stats[category] as StatStore, key, value);
	}
}

function insertCalcStat(stats: StatStore, key: string, value: number) {

	if (value === undefined || value === null) {
		value = 0;
	}

	if (typeof value !== 'number') {
		console.warn(`Invalid value for stat ${key}:`, value);
		return;
	}

	const prev = (stats[key] as CalcStat | undefined) ?? {
		count: 0,
		sum: 0,
		avg: 0,
		min: Infinity,
		max: -Infinity,
		firstValue: value,
		streak: 0,
		streakMax: 0,
		createDate: new Date().toISOString(),
		modifiedDate: new Date().toISOString(),
		lastValue: 0,
	};

	const stat: CalcStat = {
		count: prev.count + 1,
		sum: prev.sum + value,
		avg: Number(((prev.sum + value) / (prev.count + 1)).toFixed(2)),
		min: Math.min(prev.min, value),
		max: Math.max(prev.max, value),
		firstValue: prev.firstValue ?? value,
		streak: prev.lastValue === value && value !== 0 ? prev.streak + 1 : 0,
		streakMax: Math.max(prev.streakMax, prev.lastValue === value && value !== 0 ? prev.streak + 1 : 0),
		createDate: prev.createDate ?? new Date().toISOString(),
		modifiedDate: new Date().toISOString(),
		lastValue: value,
	};

	stats[key] = stat;
}
